import { useEffect, useState } from 'react';
import { reviewGiftCardRedemption } from '../services/giftCardService';

const CONFLICT_MESSAGE = 'This order was already handled';

function getActionCopy(action, petals) {
  const petalsText = petals != null ? `${new Intl.NumberFormat('en-US').format(Number(petals))} petals` : 'the petals';
  if (action === 'approve') {
    return {
      title: 'Approve — mark as sent?',
      message: 'The user will be told their gift card was sent. Only do this after Xoxoday confirms the order.',
      confirm: 'Approve',
      confirmClass: 'gift-card-review-confirm-approve',
    };
  }
  if (action === 'reject') {
    return {
      title: `Reject — refund ${petalsText}?`,
      message: `This refunds ${petalsText} to the user. This cannot be undone.`,
      confirm: 'Reject & refund',
      confirmClass: 'gift-card-review-confirm-reject',
    };
  }
  return null;
}

// Confirm step for the review panel. `onReviewed(message, { conflict })` is called on
// success (200) and on 409; the caller should refetch the order in both cases.
// On success the second argument also carries the updated `redemption`, if returned.
export default function GiftCardReviewModal({ open, redemptionId, action, note, petals, onClose, onReviewed }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setSubmitting(false);
    }
  }, [open, action, redemptionId]);

  const copy = getActionCopy(action, petals);
  if (!open || !redemptionId || !copy) return null;

  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    setError('');

    try {
      const response = await reviewGiftCardRedemption(redemptionId, { action, note: note?.trim() });
      onReviewed?.(response?.message || 'Review submitted.', {
        conflict: false,
        redemption: response?.data?.redemption || null,
      });
      onClose();
    } catch (err) {
      if (err.status === 409) {
        onReviewed?.(CONFLICT_MESSAGE, { conflict: true });
        onClose();
        return;
      }
      setError(err.message || 'Failed to submit review.');
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay gift-card-review-overlay" onClick={submitting ? undefined : onClose}>
      <div
        className="modal-content gift-card-review-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gift-card-review-title"
      >
        <h3 id="gift-card-review-title" className="modal-title">{copy.title}</h3>
        <p className="modal-message">{copy.message}</p>

        {error && <div className="add-restaurant-error">{error}</div>}

        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn-cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className={`modal-btn modal-btn-confirm ${copy.confirmClass}`}
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : copy.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
